// packages/storefront/src/translation/domGuard.ts
//
// FROZEN PROTOCOL SURFACE of the embed's DOM guard (docs/embed-browser-translation.md). Behaviour lives in
// domGuardImpl.ts; the shared state shape in domGuardState.ts.
//
// Why a guard: a browser page translator (Chrome/Edge <font> wrappers, Safari, Firefox, Google's website widget)
// replaces the text nodes React created. React keeps direct references to them, so its next commit on that subtree
// calls removeChild/insertBefore on nodes that are no longer where it left them, and the DOM throws NotFoundError
// from React's commit phase (the 2026-09-22 storefront crash). The guard patches the four Node.prototype members
// React writes its tree through, models what a translator did to OUR nodes, and completes React's calls against
// what is on screen. It never changes a call the browser would accept, never changes behaviour for nodes that are
// not ours, and never throws for ours.
//
// Contract (each rule is tested):
//  C1 A DOM call the browser would ACCEPT is performed exactly as asked (Reflect.apply on the layer below, the
//     original arguments object). Bookkeeping only.
//  C2 A call the browser would REJECT because a node created by one of OUR registered react-dom copies is not where
//     React left it is completed against what now shows that node (heal), synchronously. It never throws.
//  C3 Calls about nodes that are not ours: exact native semantics, throws included.
//  C4 A nodeValue write always lands on the node it targets, first.
//  C5 Guard failures never escape: decisions are try/caught; 20 faults trip a breaker to "safe mode" (no healing;
//     our invalid calls become no-throw).
//  C6 One install per page: frozen trampolines call reg.impl; newest impl wins; never re-wrap, never uninstall;
//     state is shared across impl versions.
//
// VERSION RULES (every bundle of every deploy that ever reached a partner page shares one registry):
//  1. DOM_GUARD_PROTOCOL, the registry key, the trampoline bodies, the observer callback and EXACT_VALUE_ATTR
//     (@tourtask/ui/components/exactValueAttr) never change. A DOM primitive React starts using later (for example
//     `moveBefore`) is added as an ADDITIONAL trampoline, idempotently (`reg.trampolines.moveBefore ??= …`), with no
//     protocol bump.
//  2. ANY change to domGuardImpl.ts or domGuardState.ts bumps DOM_GUARD_IMPL_VERSION. domGuard.install.test.ts pins
//     sha256 of both files per version and fails until the bump and the new hash are committed.
//  3. State fields are append-only: never rename, retype or remove one (initDomGuardState adds defaults; a test pins
//     the field list as a prefix).
//  4. ROLLBACK IS ROLL-FORWARD. Newest wins and the registry is non-configurable, so reverting a deploy to N-1
//     leaves impl N governing every page where a cached N bundle loads. To undo a bad impl N, ship the previous code
//     as N+1.

import { createDomGuardImpl, nodeConstructorOf } from './domGuardImpl';
import type { DomGuardImpl } from './domGuardImpl';
import { initDomGuardState } from './domGuardState';
import type { DomGuardState } from './domGuardState';

export const DOM_GUARD_REGISTRY_KEY: unique symbol = Symbol.for('tourtask.domGuard');
export const DOM_GUARD_PROTOCOL = 1;
/** Started at 1. Bump on ANY change to domGuardImpl.ts or domGuardState.ts (rule 2 above). 3: registerReactSubtree. */
export const DOM_GUARD_IMPL_VERSION = 3;

/**
 * The members of a window the guard touches. `window` satisfies it, and so does a jsdom window in tests: the guard
 * always works on the window it is given, never on globals. Node.prototype is found through `document`, never through
 * the writable `Node` binding (nodeConstructorOf).
 */
export type DomGuardWindow = Pick<Window & typeof globalThis, 'MutationObserver' | 'document' | 'queueMicrotask'>;

/** Whatever the four members were when the registry was created: native, or a foreign wrapper installed before us. */
export interface DomGuardBelow {
	readonly removeChild: Node['removeChild'];
	readonly insertBefore: Node['insertBefore'];
	readonly appendChild: Node['appendChild'];
	readonly nodeValueGet: (this: Node) => string | null;
	readonly nodeValueSet: (this: Node, value: string | null) => void;
}

/** Installed on Node.prototype once per page and never replaced (protocol 1). */
export interface DomGuardTrampolines {
	readonly removeChild: (this: unknown, child: unknown) => unknown;
	readonly insertBefore: (this: unknown, node: unknown, child: unknown) => unknown;
	readonly appendChild: (this: unknown, node: unknown) => unknown;
	readonly nodeValueSet: (this: unknown, value: unknown) => void;
}

/** Shape frozen: fields may be ADDED, never renamed or retyped. */
export interface DomGuardRegistryV1 {
	readonly protocol: 1;
	implVersion: number;
	implBuild: string;
	impl: DomGuardImpl | null;
	installed: boolean;
	readonly below: DomGuardBelow;
	readonly trampolines: DomGuardTrampolines;
	readonly mo: MutationObserver;
	readonly state: DomGuardState;
	readonly diag: { foreignBelow: string[]; notOnTop: string[] };
}

export interface DomGuardOptions {
	/** The deploy that installed the impl (telemetry: `reg.implBuild`). */
	build: string;
	/** Harness diagnostics only. Production passes nothing. */
	log?: (e: object) => void;
}

const TRAMPOLINE_METHODS = ['removeChild', 'insertBefore', 'appendChild'] as const;

/**
 * Installs the guard on `win` once per page, or adopts the page's existing registry and upgrades its impl when this
 * bundle's is newer. Returns null when the page holds a registry of an unknown protocol (never touch the prototypes
 * twice) or its DOM lacks a member the guard wraps; the widget renders unguarded then.
 */
export function ensureDomGuard(win: DomGuardWindow, opts: DomGuardOptions): DomGuardRegistryV1 | null {
	return installDomGuardAtVersion(win, opts, DOM_GUARD_IMPL_VERSION);
}

/**
 * The install algorithm with the impl version passed in. Production code calls {@link ensureDomGuard};
 * domGuard.install.test.ts passes other versions to stand in for bundles of other deploys on one page.
 */
export function installDomGuardAtVersion(win: DomGuardWindow, opts: DomGuardOptions, implVersion: number): DomGuardRegistryV1 | null {
	const NodeCtor = nodeConstructorOf(win.document);
	if (NodeCtor === null) return null; // not a DOM the guard understands
	const NP = NodeCtor.prototype;
	const existing: unknown = Reflect.get(win, DOM_GUARD_REGISTRY_KEY);
	let reg: DomGuardRegistryV1;
	if (existing === undefined) {
		const created = createRegistry(win, NP);
		if (created === null) return null;
		reg = created;
		Object.defineProperty(win, DOM_GUARD_REGISTRY_KEY, { value: reg, configurable: false, enumerable: false, writable: false });
	} else if (isRegistryV1(existing)) {
		reg = existing;
	} else {
		return null; // incompatible registry: never touch the prototypes twice
	}
	if (reg.impl === null || reg.implVersion < implVersion) {
		reg.impl = createDomGuardImpl(reg, win, opts);
		reg.implVersion = implVersion;
		reg.implBuild = opts.build;
	}
	if (!reg.installed) installTrampolines(reg, NP); // LAST: no trampoline can run without an impl
	reg.impl.checkInstall();
	return reg;
}

function isRegistryV1(x: unknown): x is DomGuardRegistryV1 {
	return typeof x === 'object' && x !== null && 'protocol' in x && x.protocol === DOM_GUARD_PROTOCOL;
}

const isNative = (fn: unknown): boolean => {
	try {
		return /\{\s*\[native code\]\s*\}\s*$/.test(Function.prototype.toString.call(fn));
	} catch {
		return false;
	}
};

function createRegistry(win: DomGuardWindow, NP: Node): DomGuardRegistryV1 | null {
	const nv = Object.getOwnPropertyDescriptor(NP, 'nodeValue');
	// Every member below is captured as a VALUE, to be called later with an explicit receiver (`.call` /
	// Reflect.apply), never bare. Reflect.get reads it as exactly that, so no `unbound-method` exemption is needed.
	const nodeValueGet = nv === undefined ? undefined : (Reflect.get(nv, 'get') as ((this: Node) => string | null) | undefined);
	const nodeValueSet = nv === undefined ? undefined : (Reflect.get(nv, 'set') as ((this: Node, value: string | null) => void) | undefined);
	if (nodeValueGet === undefined || nodeValueSet === undefined) return null; // not a DOM the guard understands
	const below: DomGuardBelow = {
		removeChild: Reflect.get(NP, 'removeChild'),
		insertBefore: Reflect.get(NP, 'insertBefore'),
		appendChild: Reflect.get(NP, 'appendChild'),
		nodeValueGet,
		nodeValueSet,
	};
	// FROZEN PROTOCOL v1: these functions and the observer callback are never replaced. Each receives the caller's
	// own `arguments` object so the arity reaches the layer below unchanged (C1: `insertBefore(node)` with one
	// argument must fail exactly as natively). A missing impl (impossible by install order; only a third party
	// clearing `reg.impl` could cause it) degrades to the layer below, never to a page-wide TypeError.
	const trampolines: DomGuardTrampolines = {
		removeChild: function removeChild(this: unknown, child: unknown): unknown {
			// eslint-disable-next-line prefer-rest-params
			const args = arguments;
			const impl = reg.impl;
			return impl === null ? (Reflect.apply(below.removeChild, this, args) as unknown) : impl.removeChild(this, child, args);
		},
		insertBefore: function insertBefore(this: unknown, node: unknown, child: unknown): unknown {
			// eslint-disable-next-line prefer-rest-params
			const args = arguments;
			const impl = reg.impl;
			return impl === null ? (Reflect.apply(below.insertBefore, this, args) as unknown) : impl.insertBefore(this, node, child, args);
		},
		appendChild: function appendChild(this: unknown, node: unknown): unknown {
			// eslint-disable-next-line prefer-rest-params
			const args = arguments;
			const impl = reg.impl;
			return impl === null ? (Reflect.apply(below.appendChild, this, args) as unknown) : impl.appendChild(this, node, args);
		},
		nodeValueSet: function (this: unknown, value: unknown): void {
			// eslint-disable-next-line prefer-rest-params
			const args = arguments;
			const impl = reg.impl;
			if (impl === null) Reflect.apply(below.nodeValueSet, this, args);
			else impl.setNodeValue(this, value, args);
		},
	};
	for (const k of TRAMPOLINE_METHODS) Object.defineProperty(trampolines[k], 'name', { value: k }); // survive minification
	Object.defineProperty(trampolines.nodeValueSet, 'name', { value: 'set nodeValue' });
	// telemetry only: a foreign wrapper below us receives only calls the browser accepts
	const foreignBelow: string[] = TRAMPOLINE_METHODS.filter((k) => !isNative(below[k]));
	if (!isNative(nodeValueSet)) foreignBelow.push('nodeValue');
	const reg: DomGuardRegistryV1 = {
		protocol: DOM_GUARD_PROTOCOL,
		implVersion: 0,
		implBuild: '',
		impl: null,
		installed: false,
		below,
		trampolines,
		mo: new win.MutationObserver((records) => {
			const impl = reg.impl;
			if (impl !== null) impl.process(records);
		}),
		state: initDomGuardState({}),
		diag: { foreignBelow, notOnTop: [] },
	};
	return reg;
}

/**
 * Puts the trampolines on Node.prototype, copying each member's descriptor attributes. A member that cannot be
 * redefined (a page that froze Node.prototype) is left as it is: checkInstall then reports it as `not-on-top`, and
 * `installed` stays false so a later bundle tries again. Installing never throws into the widget's mount.
 *
 * Never re-wraps (C6): a member is only ever replaced while it still holds exactly what the registry captured as
 * `below`. A retry after a partial install therefore skips the members that are already our trampolines, and never
 * strips a wrapper a page installed above us in between (a consent manager, a #11538 snippet): that one stays on top
 * and is reported as `not-on-top`.
 */
function installTrampolines(reg: DomGuardRegistryV1, NP: Node): void {
	let all = true;
	for (const k of TRAMPOLINE_METHODS) {
		const d = Object.getOwnPropertyDescriptor(NP, k);
		const current: unknown = d?.value;
		if (current === reg.trampolines[k]) continue;
		if (d === undefined || current !== reg.below[k] || !redefine(NP, k, { value: reg.trampolines[k], writable: d.writable, enumerable: d.enumerable, configurable: d.configurable })) all = false;
	}
	const nv = Object.getOwnPropertyDescriptor(NP, 'nodeValue');
	// the accessor pair is read as values: to compare it, and to carry the getter over unchanged (never called here)
	const getNow = nv === undefined ? undefined : Reflect.get(nv, 'get');
	const setNow: unknown = nv === undefined ? undefined : Reflect.get(nv, 'set');
	if (setNow !== reg.trampolines.nodeValueSet) {
		if (nv === undefined || setNow !== reg.below.nodeValueSet || !redefine(NP, 'nodeValue', { get: getNow, set: reg.trampolines.nodeValueSet, enumerable: nv.enumerable, configurable: nv.configurable })) all = false;
	}
	reg.installed = all;
}

function redefine(NP: Node, key: string, descriptor: PropertyDescriptor): boolean {
	try {
		Object.defineProperty(NP, key, descriptor);
		return true;
	} catch {
		return false; // non-configurable (a frozen Node.prototype): reported by checkInstall as not-on-top
	}
}
